import { type AnyAction, combineReducers } from 'redux';

import type { Blueprint, PublicBlueprintList } from 'js/src/client/types.gen';

type BlueprintItem = PublicBlueprintList | Blueprint;
import {
    pushBlueprint,
    pushBlueprints,
    removeBlueprint,
    setBlueprintName,
    setBlueprintsLoading,
} from 'js/src/data/blueprints/blueprints-actions';

const updateBlueprintList = (state: BlueprintItem[], blueprints: BlueprintItem[]) => {
    const blueprintMap = new Map();

    const existingBlueprintIdsSet = new Set(state.map((b) => b.id));

    const newBlueprints = blueprints.filter(
        (blueprint) => !existingBlueprintIdsSet.has(blueprint.id),
    );
    const existingBlueprintsToUpdate = blueprints.filter((blueprint) =>
        existingBlueprintIdsSet.has(blueprint.id),
    );
    // Add new blueprints first
    newBlueprints.forEach((blueprint) => {
        blueprintMap.set(blueprint.id, blueprint);
    });
    // Then add existing blueprints from current state
    state.forEach((blueprint) => {
        blueprintMap.set(blueprint.id, blueprint);
    });
    // Then update existing blueprints
    existingBlueprintsToUpdate.forEach((blueprint) => {
        blueprintMap.set(blueprint.id, blueprint);
    });

    return Array.from(blueprintMap.values());
};

export function blueprintList(state: BlueprintItem[] = [], action: AnyAction): BlueprintItem[] {
    if (removeBlueprint.match(action)) {
        return state.filter((b) => b.id !== action.payload.blueprintId);
    }

    if (pushBlueprint.match(action)) {
        return updateBlueprintList(state, [action.payload.blueprint]);
    }

    if (pushBlueprints.match(action)) {
        return updateBlueprintList(state, action.payload.blueprints);
    }

    if (setBlueprintName.match(action)) {
        const { name, blueprintId } = action.payload;
        const index = state.findIndex((b) => b.id === blueprintId);

        if (index === -1) {
            return state;
        }

        const newState = [...state];
        newState[index] = { ...newState[index], name };
        return newState;
    }

    return state;
}

export function isLoading(state: boolean = false, action: AnyAction): boolean {
    if (setBlueprintsLoading.match(action)) {
        return action.payload.loading;
    }
    return state;
}

export const blueprints = combineReducers({
    blueprintList,
    isLoading,
});

export type BlueprintsState = ReturnType<typeof blueprints>;
